import { storage, settings } from './storage.js';
import { profiles } from './profiles.js';
import { viewers } from './viewers.js';
import { catalog } from './catalog.js';
import { device } from './device.js';
import { Emitter } from './utils.js';
import { i18n } from './i18n.js';

/* Cuenta Filoteca: login con Google contra server/api.js, sesión por token y perfiles IPTV
   sincronizados con el servidor (cifrados allí). Si el servidor no tiene cuentas activadas
   (sin GOOGLE_CLIENT_ID) la app funciona como antes, solo con perfiles locales. */

const DEFAULT_SERVER = 'https://filoteca.com';
const GIS_SRC = 'https://accounts.google.com/gsi/client';

class Account extends Emitter {
    constructor() {
        super();
        this.enabled = false;
        this.clientId = null;
        this.token = storage.get('authToken', '') || '';
        this.user = storage.get('authUser', null);
        this.ready = false;
        this.online = true;
        this.registration = 'invite';
        this.passwordAuth = false;
        this.terms = null;
        this.streamToken = '';
        this._streamTimer = null;
        this.invite = this._readInvite();
    }

    /** Invitación por enlace (?invite=…): se guarda en el dispositivo y se quita de la barra de direcciones. */
    _readInvite() {
        let found = '';
        try {
            const q = new URLSearchParams(location.search);
            found = (q.get('invite') || '').trim();
            if (q.has('invite') && window.history && history.replaceState) {
                q.delete('invite');
                const rest = q.toString();
                history.replaceState(history.state, '', location.pathname + (rest ? '?' + rest : '') + location.hash);
            }
        } catch (e) { /* sin location (pruebas) */ }
        if (/^[A-Za-z0-9_-]{16,64}$/.test(found)) storage.set('invite', found);
        const saved = storage.get('invite', '') || '';
        return /^[A-Za-z0-9_-]{16,64}$/.test(saved) ? saved : '';
    }

    clearInvite() {
        this.invite = '';
        storage.remove('invite');
    }

    /** { valid, from } de la invitación guardada; null si no hay o no se pudo comprobar. */
    async checkInvite() {
        if (!this.invite) return null;
        try {
            return await this.request('POST', '/api/invite/check', { auth: false, body: { token: this.invite } });
        } catch (e) {
            return null;
        }
    }

    /** Hay términos de uso vigentes que este usuario aún no ha aceptado. */
    get termsPending() {
        return !!(this.signedIn && this.user.terms && !this.user.terms.accepted);
    }

    get signedIn() {
        return !!(this.enabled && this.token && this.user);
    }

    /** Sesión de la demo pública (cuenta compartida de solo lectura que abre la landing). */
    get demo() {
        return !!(this.signedIn && this.user && this.user.demo);
    }

    /** ?demo=1 en la dirección: la landing abre la app en modo demo; ?shell= y ?tv= eligen aspecto y modo tele para la galería. */
    _readDemoParams() {
        try {
            const q = new URLSearchParams(location.search);
            if (q.get('demo') !== '1') return null;
            const out = { shell: String(q.get('shell') || '').slice(0, 30), tv: q.get('tv'), key: '' };
            // Clave de pruebas del administrador (quita el tope de la demo): va en el fragmento (#dk=…), que no se envía al servidor.
            const m = /(?:^|[#&])dk=([A-Za-z0-9_-]{16,200})/.exec(location.hash || '');
            if (m) { out.key = m[1]; try { sessionStorage.setItem('demoKey', m[1]); } catch (e) { /* sin sessionStorage */ } } else { try { out.key = sessionStorage.getItem('demoKey') || ''; } catch (e) { /* sin sessionStorage */ } }
            ['demo', 'shell', 'tv'].forEach((k) => q.delete(k));
            if (window.history && history.replaceState) history.replaceState(null, '', location.pathname + (q.toString() ? '?' + q.toString() : ''));
            return out;
        } catch (e) { return null; }
    }

    /** ¿La demo de este navegador ya terminó (por tiempo o por cupo)? Se recuerda un día, para no abrir otra sin más. */
    get demoEnded() {
        const at = Number(storage.get('demoEndedAt') || 0);
        return at > 0 && Date.now() - at < 86400000;
    }

    /** Cuándo caduca la sesión de la demo (0 si no hay). */
    get demoExpiresAt() {
        return this.demo && !this.demoUnlimited ? Number(storage.get('demoExpiresAt') || 0) : 0;
    }

    /** Sesión de pruebas del administrador (clave): sin tope de tiempo. */
    get demoUnlimited() {
        return !!(this.demo && storage.get('demoUnlimited'));
    }

    endDemo() {
        storage.set('demoEndedAt', Date.now());
        storage.remove('demoExpiresAt');
        this.demoJustEnded = true;
        this.emit('change');
    }

    /** Pide al servidor una sesión de la demo y la aplica. Devuelve true si se entró. */
    async enterDemo(params = {}) {
        if (this.demoEnded && !params.key) return false;
        let data;
        try {
            data = await this.request('POST', '/api/auth/demo', { auth: false, body: { device: device.deviceLabel(), key: params.key || undefined } });
        } catch (e) {
            if (e && e.code === 'demo_limit') { storage.set('demoEndedAt', Date.now()); this.demoLimited = true; }
            throw e;
        }
        if (!data || !data.token) return false;
        // Con la clave de pruebas no hay cuenta atrás ni fin: la sesión dura horas y no se recuerda ningún fin anterior.
        if (data.unlimited) storage.remove('demoExpiresAt'); else storage.set('demoExpiresAt', Number(data.expiresAt) || (Date.now() + 3 * 60000));
        storage.set('demoUnlimited', !!data.unlimited);
        storage.remove('demoEndedAt');
        this._setSession(data);
        // La demo no pasa por la bienvenida; aspecto y modo tele vienen de la galería de la landing.
        storage.set('welcomeDone', true);
        // Las dos fuentes de la demo, unidas en un solo catálogo (Cine clásico como principal y la lista de canales sumada).
        const list = profiles.list();
        const main = list.find((p) => p.type === 'archive') || list[0];
        if (main) storage.set('currentProfile', main.id);
        storage.set('includedProfiles', list.filter((p) => !main || p.id !== main.id).map((p) => p.id));
        this._applyDemoParams(params);
        return true;
    }

    /** Aspecto y modo tele pedidos por la galería de la landing; main.js vuelve a aplicar los ajustes de interfaz si cambiaron. */
    _applyDemoParams(params = {}) {
        let changed = false;
        if (params.shell && params.shell !== settings.get('shell')) { settings.set('shell', params.shell); changed = true; }
        const tv = params.tv === '1' ? 'on' : params.tv === '0' ? 'off' : '';
        if (tv && tv !== settings.get('tvMode')) { settings.set('tvMode', tv); changed = true; }
        if (changed) this.demoUiChanged = true;
    }

    get isAdmin() {
        return this.signedIn && this.user.role === 'admin';
    }

    /** Base de la API: mismo origen en web; en apps nativas el servidor configurado (o filoteca.com). */
    baseUrl() {
        const custom = (settings.get('serverUrl') || '').trim().replace(/\/+$/, '');
        if (custom) return custom;
        if (/^https?:/.test(location.protocol) && !device.isCordova && !device.isElectron) return location.origin;
        return DEFAULT_SERVER;
    }

    /** Puede usar el botón oficial de Google (solo en web servida por http/https). */
    canUseGoogleButton() {
        return /^https?:/.test(location.protocol) && !device.isCordova && !device.isElectron;
    }

    async init() {
        const demoParams = this._readDemoParams();
        // Esta carga viene de la demo (?demo=1): solo entonces tiene sentido enseñar «la demo ha terminado». Se recuerda en la pestaña/marco
        // (sessionStorage) porque la app reescribe la dirección y, al recargar el marco, el ?demo=1 ya no está.
        let framedDemo = false;
        try {
            const framed = window.top !== window; // solo en el marco de la landing: quien abre /app/ a secas en su pestaña debe poder iniciar sesión
            if (demoParams && framed) sessionStorage.setItem('iptv:demoEntry', '1');
            framedDemo = framed && sessionStorage.getItem('iptv:demoEntry') === '1';
        } catch (e) { /* sin sessionStorage, o marco de otro origen */ }
        this.demoEntry = !!demoParams || framedDemo;
        try {
            const cfg = await this.request('GET', '/api/config', { auth: false, timeout: 6000 });
            this.enabled = !!cfg.auth;
            this.demoAvailable = !!cfg.demo;
            // Una demo caducada que sigue guardada no vale: se olvida antes de decidir si se abre otra.
            if (this.user && this.user.demo && this.demoExpiresAt && this.demoExpiresAt <= Date.now()) { this._clearSession(); this.endDemo(); }
            // Una sesión de pruebas guardada caduca a las horas del servidor: si el servidor ya no la reconoce, se limpia sola (sync → 401).
            if (demoParams && cfg.auth && cfg.demo && !(this.token && this.user && this.user.demo)) {
                try { await this.enterDemo(demoParams); } catch (e) { /* sin demo: sigue como siempre */ }
            } else if (demoParams && this.user && this.user.demo) {
                this._applyDemoParams(demoParams);
            }
            this.clientId = cfg.googleClientId || null;
            this.serverVersion = cfg.version || '';
            this.apkVersion = cfg.apkVersion || '';
            this.transcode = !!cfg.transcode;
            this.tmdb = !!cfg.tmdb;
            this.omdb = !!cfg.omdb;
            this.subtitles = !!cfg.subtitles;
            this.support = !!cfg.support;
            this.ads = cfg.ads || null;
            this.recordings = cfg.recordings || null;
            this.registration = cfg.registration || 'invite';
            this.passwordAuth = !!cfg.passwordAuth;
            this.terms = cfg.terms || null;
            this.online = true;
        } catch (e) {
            // Sin red o servidor no accesible: si había sesión, seguimos con los perfiles en caché.
            this.online = false;
            this.enabled = !!this.token;
        }
        if (this.enabled) {
            profiles.remote = this;
            viewers.remote = this;
            catalog.sync = this.signedIn ? this : null;
            if (this.token && this.online) {
                try {
                    await this.sync();
                } catch (e) {
                    if (e.status === 401 || (e.status === 403 && e.code !== 'terms_required')) { const wasDemo = this.demo; this._clearSession(); if (wasDemo) this.endDemo(); } // demo guardada que el servidor ya no reconoce: terminada, no «inicia sesión»
                }
                await this.refreshStreamToken();
            }
        }
        this.ready = true;
        return this;
    }

    async request(method, path, { body, auth = true, timeout = 15000 } = {}) {
        const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
        const timer = setTimeout(() => controller && controller.abort(), timeout);
        const headers = { Accept: 'application/json' };
        if (body) headers['Content-Type'] = 'application/json';
        if (auth && this.token) headers.Authorization = 'Bearer ' + this.token;
        let res;
        try {
            res = await fetch(this.baseUrl() + path, { method, headers, body: body ? JSON.stringify(body) : undefined, cache: 'no-store', signal: controller ? controller.signal : undefined });
        } finally {
            clearTimeout(timer);
        }
        let data = {};
        try { data = await res.json(); } catch (e) { /* sin cuerpo */ }
        if (!res.ok) {
            const err = new Error(data.error || 'HTTP ' + res.status);
            err.status = res.status;
            err.code = data.error;
            if (auth && res.status === 401 && this.token) {
                // Sesión de la demo caducada o perdida: la demo ha terminado; nunca se enseña el inicio de sesión.
                const wasDemo = this.demo;
                this._clearSession();
                if (wasDemo) { this.endDemo(); this.emit('demoended'); } else this.emit('signedout');
            }
            if (res.status === 403 && data.error === 'terms_required') this.emit('terms');
            throw err;
        }
        return data;
    }

    _setSession({ token, user, profiles: list, viewers: viewerList }) {
        this.token = token;
        this.user = user;
        storage.set('authToken', token);
        storage.set('authUser', user);
        this.clearInvite();
        this._applyProfiles(list || []);
        this._applyViewers(viewerList || []);
        catalog.sync = this;
        this.emit('change');
        this.refreshStreamToken();
    }

    /**
     * Enlace de reproducción: <video> y hls.js no pueden enviar la cabecera de sesión, así que /proxy
     * recibe un testigo de vida limitada (?st=) que solo vale para eso. Se renueva cada 12 horas.
     */
    async refreshStreamToken() {
        clearTimeout(this._streamTimer);
        if (!this.signedIn || !this.online || this.termsPending) {
            this.streamToken = '';
            return '';
        }
        try {
            const r = await this.request('GET', '/api/stream-token');
            this.streamToken = r.token || '';
        } catch (e) {
            if (e.status === 401) this.streamToken = '';
        }
        this._streamTimer = setTimeout(() => this.refreshStreamToken(), 12 * 3600 * 1000);
        return this.streamToken;
    }

    /** Acepta la versión vigente de los términos de uso. */
    async acceptTerms() {
        const r = await this.request('POST', '/api/terms/accept', { body: { version: this.user.terms.version } });
        this.user = r.user;
        storage.set('authUser', r.user);
        this.emit('change');
        await this.refreshStreamToken();
    }

    /** Borra la cuenta y todos sus datos en el servidor. El cierre de sesión local lo hace quien llama. */
    async deleteAccount() {
        await this.request('DELETE', '/api/me', { body: { confirm: this.user.email } });
    }

    _clearSession() {
        // La landing en pruebas se abre con una cookie de administrador: al perder la sesión, se cierra también.
        try { if (this.token && typeof fetch === 'function' && /^https?:/.test(location.protocol)) fetch(this.baseUrl() + '/api/landing/lock', { method: 'POST', keepalive: true }).catch(() => {}); } catch (e) { /* sin red */ }
        this.token = '';
        this.user = null;
        storage.remove('authToken');
        storage.remove('authUser');
        catalog.sync = null;
        this.emit('change');
    }

    _applyViewers(list) {
        if (!list.length) return;
        const currentId = storage.get('viewerId');
        viewers.save(list);
        if (!list.some((v) => v.id === currentId)) storage.remove('viewerId');
    }

    _applyProfiles(list) {
        // Proveedores que otra persona acaba de compartir contigo: se avisa una sola vez por cada uno.
        const seen = storage.get('sharedSeen') || [];
        const fresh = list.filter((p) => p.shared && seen.indexOf(p.id) === -1);
        if (fresh.length) {
            storage.set('sharedSeen', seen.concat(fresh.map((p) => p.id)).slice(-200));
            setTimeout(() => this.emit('newshared', fresh), 0);
        }
        const currentId = storage.get('currentProfile');
        profiles.save(list.map((p) => Object.assign({ createdAt: p.createdAt || Date.now() }, p)));
        if (!list.some((p) => p.id === currentId)) {
            storage.remove('currentProfile');
            if (list[0]) profiles.setCurrent(list[0].id);
        }
    }

    /** Descarga usuario y perfiles del servidor (otros dispositivos pueden haberlos cambiado). */
    async sync() {
        const me = await this.request('GET', '/api/me');
        this.user = me.user;
        storage.set('authUser', me.user);
        this._applyProfiles(me.profiles || []);
        this._applyViewers(me.viewers || []);
        this.emit('change');
        return me;
    }

    /* ---------------- Google ---------------- */

    loadGoogle() {
        if (window.google && window.google.accounts) return Promise.resolve(window.google);
        if (this._gis) return this._gis;
        this._gis = new Promise((resolve, reject) => {
            const s = document.createElement('script');
            s.src = GIS_SRC;
            s.async = true;
            s.defer = true;
            s.onload = () => resolve(window.google);
            s.onerror = () => { this._gis = null; reject(new Error('gis')); };
            document.head.appendChild(s);
        });
        return this._gis;
    }

    /**
     * Pinta el botón oficial de Google en `container` y resuelve cuando el login termina.
     * onResult(err|null) se llama tras cada intento.
     */
    async renderGoogleButton(container, onResult) {
        const google = await this.loadGoogle();
        google.accounts.id.initialize({
            client_id: this.clientId,
            ux_mode: 'popup',
            auto_select: false,
            itp_support: true,
            callback: async (resp) => {
                try {
                    await this.signInWithCredential(resp.credential);
                    onResult(null);
                } catch (err) {
                    onResult(err);
                }
            }
        });
        google.accounts.id.renderButton(container, {
            theme: 'filled_black',
            size: 'large',
            shape: 'pill',
            width: Math.min(360, Math.max(200, container.clientWidth || 300)),
            text: 'continue_with',
            logo_alignment: 'left'
        });
    }

    async signInWithCredential(credential) {
        const data = await this.request('POST', '/api/auth/google', { auth: false, body: { credential, device: device.deviceLabel(), invite: this.invite } });
        return this._finishLogin(data);
    }

    /** Si el servidor pide el segundo paso (administradores con verificación en dos pasos), pregunta el código y abre la sesión con él;
        si se cancela, lanza twofa_cancelled. Sin segundo paso, abre la sesión directamente. */
    async _finishLogin(data) {
        let tries = 0;
        while (data && data.twofa) {
            if (!this.askSecondFactor) throw Object.assign(new Error('twofa_unsupported'), { code: 'twofa_unsupported' });
            const code = await this.askSecondFactor({ retry: tries > 0 });
            if (!code) throw Object.assign(new Error('twofa_cancelled'), { code: 'twofa_cancelled' });
            try {
                data = await this.request('POST', '/api/auth/2fa', { auth: false, body: { challenge: data.challenge, code } });
            } catch (e) {
                if (e && e.code === 'invalid_code' && ++tries < 5) continue; // se vuelve a preguntar con el mismo desafío
                throw e;
            }
        }
        this._setSession(data);
        if (data && data.recoveryUsed && this.onRecoveryUsed) this.onRecoveryUsed(data.recoveryLeft);
        return data;
    }

    /* ---------------- Cuenta propia: correo y contraseña ---------------- */

    /** Crea el alta y envía un código al correo. La cuenta no existe hasta verificarlo. */
    register({ email, password, name }) {
        return this.request('POST', '/api/auth/register', { auth: false, body: { email, password, name, lang: i18n.lang, invite: this.invite } });
    }

    /** Verifica el correo con el código recibido: crea la cuenta y abre sesión. */
    async verifyEmail({ email, code }) {
        const data = await this.request('POST', '/api/auth/verify', { auth: false, body: { email, code, device: device.deviceLabel(), invite: this.invite } });
        return this._finishLogin(data);
    }

    async loginWithPassword({ email, password }) {
        const data = await this.request('POST', '/api/auth/login', { auth: false, body: { email, password, device: device.deviceLabel() } });
        return this._finishLogin(data);
    }

    forgotPassword({ email }) {
        return this.request('POST', '/api/auth/forgot', { auth: false, body: { email, lang: i18n.lang } });
    }

    async resetPassword({ email, code, password }) {
        const data = await this.request('POST', '/api/auth/reset', { auth: false, body: { email, code, password, device: device.deviceLabel() } });
        return this._finishLogin(data);
    }

    /** Cambia (o crea, si solo entraba con Google) la contraseña de la cuenta con sesión abierta. */
    async changePassword({ current, password }) {
        const r = await this.request('POST', '/api/me/password', { body: { current, password } });
        this.user = r.user;
        storage.set('authUser', r.user);
        this.emit('change');
    }

    async signOut() {
        if (this.demo) return; // la demo no se cierra a mano: termina sola
        if (this.token) {
            try { await this.request('POST', '/api/auth/logout'); } catch (e) { /* sesión ya inválida */ }
        }
        this._clearSession();
        profiles.save([]);
        viewers.save([]);
        storage.remove('currentProfile');
        storage.remove('viewerId');
        this.emit('signedout');
    }

    /* ---------------- Perfiles (hook usado por profiles.js) ---------------- */

    async upsertProfile(profile) {
        if (!this.signedIn) return profile;
        const data = await this.request('POST', '/api/profiles', { body: { profile } });
        return data.profile;
    }

    async removeProfile(id) {
        if (!this.signedIn) return;
        await this.request('DELETE', '/api/profiles/' + encodeURIComponent(id));
    }

    /* ---------------- Proveedores compartidos con otra cuenta ---------------- */

    async profileShares(id) {
        return (await this.request('GET', '/api/profiles/' + encodeURIComponent(id) + '/shares')).shares || [];
    }

    async shareProfile(id, email) {
        return (await this.request('POST', '/api/profiles/' + encodeURIComponent(id) + '/shares', { body: { email, lang: (typeof navigator !== 'undefined' && navigator.language) || '' } })).shares || [];
    }

    /** Quita a una persona (lo hace el dueño) o, con el correo propio, deja de ver un proveedor compartido. */
    async unshareProfile(id, email) {
        await this.request('DELETE', '/api/profiles/' + encodeURIComponent(id) + '/shares/' + encodeURIComponent(email));
    }

    /** Aviso de «sigo reproduciendo este proveedor» (con `stop` se libera la plaza). Devuelve { evict }. */
    async playingBeat(body) {
        return this.request('POST', '/api/playing', { body, timeout: 8000 });
    }

    /* ---------------- Espectadores y sus datos (hook usado por viewers.js y catalog.js) ---------------- */

    async upsertViewer(viewer) {
        if (!this.signedIn) return viewer;
        const data = await this.request('POST', '/api/viewers', { body: { viewer } });
        viewers.save(data.viewers);
        return data.viewer;
    }

    async removeViewer(id) {
        if (!this.signedIn) return;
        const data = await this.request('DELETE', '/api/viewers/' + encodeURIComponent(id));
        if (!data.ok) throw new Error('last_viewer');
    }

    async clearViewerData(viewerId) {
        if (!this.signedIn) return;
        await this.request('DELETE', '/api/viewers/' + encodeURIComponent(viewerId) + '/data');
    }

    async pullUserData(viewerId, profileId) {
        if (!this.signedIn || !this.online) return null;
        const r = await this.request('GET', '/api/viewers/' + encodeURIComponent(viewerId) + '/data?profile=' + encodeURIComponent(profileId));
        return r.data || {};
    }

    async pushUserData(viewerId, profileId, key, data) {
        if (!this.signedIn) return;
        await this.request('PUT', '/api/viewers/' + encodeURIComponent(viewerId) + '/data/' + key + '?profile=' + encodeURIComponent(profileId), { body: { data } });
    }

    /* ---------------- Emparejamiento de TV ---------------- */

    async startPairing() {
        return this.request('POST', '/api/pair/start', { auth: false, body: { device: device.deviceLabel() } });
    }

    /** Ficha completada con TMDB a través del servidor (reparto, director, sinopsis, fondo, edad…). */
    async tmdbLookup({ kind, title, year, tmdb }) {
        if (!this.tmdb) return null;
        const qs = new URLSearchParams({ kind: kind || 'vod', title: title || '', year: year || '', tmdb: tmdb || '' });
        const r = await this.request('GET', '/api/tmdb/lookup?' + qs.toString(), { auth: this.enabled, timeout: 20000 });
        return r.info || {};
    }

    /** Entra en un Jellyfin o Emby a través del servidor (cuando el navegador no puede llegar). */
    async mediaServerAuth(body) {
        try {
            return await this.request('POST', '/api/mediaserver/auth', { body, auth: this.enabled, timeout: 20000 });
        } catch (e) {
            throw Object.assign(new Error(e.code === 'auth' ? 'auth' : 'unreachable'), { code: e.code === 'auth' ? 'auth' : 'unreachable', status: e.status });
        }
    }

    /** Contenido de una carpeta de archivos propios, leído por el servidor. */
    async filesFetch(body) {
        try {
            return await this.request('POST', '/api/files/fetch', { body, auth: this.enabled, timeout: 25000 });
        } catch (e) {
            const code = e.code === 'private_address' ? 'private_address' : 'unreachable';
            throw Object.assign(new Error(code), { code, status: e.status });
        }
    }

    /** Subtítulos disponibles para una película o un episodio (OpenSubtitles, a través del servidor). */
    async subtitleSearch(params) {
        if (!this.subtitles) return [];
        const qs = new URLSearchParams();
        Object.keys(params).forEach((k) => { if (params[k] != null && params[k] !== '') qs.set(k, params[k]); });
        const r = await this.request('GET', '/api/subtitles/search?' + qs.toString(), { auth: this.enabled, timeout: 20000 });
        return r.items || [];
    }

    /** Texto de un subtítulo. La primera vez gasta una descarga del cupo diario del servidor. */
    async subtitleFile(id) {
        const r = await this.request('GET', '/api/subtitles/file?id=' + encodeURIComponent(id), { auth: this.enabled, timeout: 30000 });
        return r.file || null;
    }

    /** Tendencias de la semana según TMDB (películas y series, por orden). */
    /** Filmografía de un actor o director según TMDB: { tmdbId, name, photo, credits: [{ kind, title, originalTitle, year, role, character }] }. */
    async tmdbPerson(name) {
        if (!this.tmdb) return {};
        const r = await this.request('GET', '/api/tmdb/person?name=' + encodeURIComponent(name || ''), { auth: this.enabled, timeout: 20000 });
        return r.person || {};
    }

    /** Valoraciones y premios de OMDb (si el servidor tiene su clave). */
    async omdbLookup({ kind, title, year, imdb }) {
        if (!this.omdb) return null;
        const qs = new URLSearchParams({ kind: kind || 'vod', title: title || '', year: year || '', imdb: imdb || '' });
        const r = await this.request('GET', '/api/omdb/lookup?' + qs.toString(), { auth: this.enabled, timeout: 20000 });
        return r.info || {};
    }

    async tmdbSearch({ kind, query, year }) {
        if (!this.tmdb) return { results: [] };
        const qs = new URLSearchParams({ kind: kind || 'vod', query: query || '', year: year || '' });
        const r = await this.request('GET', '/api/tmdb/search?' + qs.toString(), { auth: this.enabled, timeout: 20000 });
        return r || { results: [] };
    }

    async tmdbSeason({ tmdb, season }) {
        if (!this.tmdb) return {};
        const r = await this.request('GET', '/api/tmdb/season?tmdb=' + encodeURIComponent(tmdb) + '&season=' + encodeURIComponent(season), { auth: this.enabled, timeout: 20000 });
        return r.season || {};
    }

    /** «Top 10»: avisa de que se ha visto un título. Solo con cuenta; nunca molesta (si falla, se ignora). */
    async recordPlay(body) {
        if (!this.enabled || !this.signedIn) return false;
        const r = await this.request('POST', '/api/plays', { body, timeout: 8000 });
        return !!(r && r.ok);
    }

    /** Lo más visto de la semana en este servidor: [{ kind, key, viewers }]. */
    async topPlays() {
        if (!this.enabled || !this.signedIn) return [];
        const r = await this.request('GET', '/api/top?days=7&limit=40', { auth: true, timeout: 12000 });
        return (r && r.items) || [];
    }

    async tmdbTrending() {
        if (!this.tmdb) return [];
        const r = await this.request('GET', '/api/tmdb/trending', { auth: this.enabled, timeout: 20000 });
        return r.items || [];
    }

    /* ---------------- Grabaciones ---------------- */

    get canRecord() {
        return !!(this.recordings && (!this.enabled || this.signedIn));
    }

    async listRecordings() {
        return this.request('GET', '/api/recordings', { auth: this.enabled });
    }

    async scheduleRecording(data) {
        return this.request('POST', '/api/recordings', { auth: this.enabled, body: data });
    }

    async deleteRecording(id) {
        return this.request('DELETE', '/api/recordings/' + encodeURIComponent(id), { auth: this.enabled });
    }

    /** Para una grabación en curso y guarda lo grabado hasta ahora. */
    stopRecording(id) {
        return this.request('POST', '/api/recordings/' + encodeURIComponent(id) + '/stop', { auth: this.enabled });
    }

    recordingUrl(id) {
        return this.baseUrl() + '/recordings/' + encodeURIComponent(id) + '/stream' + (this.token ? '?token=' + encodeURIComponent(this.token) : '');
    }

    /* ---------------- Dispositivos (sesiones) ---------------- */

    async listSessions() {
        return (await this.request('GET', '/api/sessions')).sessions || [];
    }

    async revokeSession(id) {
        return (await this.request('DELETE', '/api/sessions/' + encodeURIComponent(id))).ok;
    }

    async renameSession(id, name) {
        return (await this.request('PATCH', '/api/sessions/' + encodeURIComponent(id), { body: { device: name } })).ok;
    }

    /** Sondea hasta que el móvil reclame el código; devuelve la sesión o null si caduca/cancela. */
    async waitPairing(pollToken, { interval = 3000, isCancelled = () => false } = {}) {
        while (!isCancelled()) {
            let r;
            try {
                r = await this.request('GET', '/api/pair/poll?token=' + encodeURIComponent(pollToken), { auth: false });
            } catch (e) {
                r = { status: 'pending' };
            }
            if (r.status === 'ok') {
                this._setSession(r);
                return r;
            }
            if (r.status === 'expired') return null;
            await new Promise((res) => setTimeout(res, interval));
        }
        return null;
    }

    async claimPairing(code) {
        return this.request('POST', '/api/pair/claim', { body: { code } });
    }

    /* ---------------- Administración ---------------- */

    async listUsers() {
        return (await this.request('GET', '/api/admin/users')).users;
    }

    async addUser(email, role) {
        return (await this.request('POST', '/api/admin/users', { body: { email, role } })).user;
    }

    async updateUser(email, patch) {
        return (await this.request('PATCH', '/api/admin/users/' + encodeURIComponent(email), { body: patch })).user;
    }

    async removeUser(email) {
        return this.request('DELETE', '/api/admin/users/' + encodeURIComponent(email));
    }

    async listInvites() {
        return (await this.request('GET', '/api/admin/invites')).invites;
    }

    /** Crea una invitación; devuelve { invite, url }. El enlace solo se puede ver ahora. */
    async createInvite({ days, uses, note }) {
        const r = await this.request('POST', '/api/admin/invites', { body: { days, uses, note } });
        return { invite: r.invite, url: this.baseUrl() + '/app/?invite=' + r.token };
    }

    async revokeInvite(id) {
        return this.request('DELETE', '/api/admin/invites/' + encodeURIComponent(id));
    }
}

export const account = new Account();
